<template>
  <div class="c-user-details">
    <div class="c-user-details_content">
      <form novalidate>
        <div class="c-form__group">
          <form-select
            name="title"
            :id="'aboutYouTitle'"
            :label="aboutYouTitleLabel"
            :options="titleOptions"
            :required="true"
            :invalid="touched['title'] && titleError"
            :valid="touched['title'] && title.length && !titleError"
            :validationMessage="titleError ? 'Please select your title' : null"
            :value="innerTitle"
            @change="valueChange($event.value, 'title')"
          >
          </form-select>
        </div>

        <div class="c-form__group">
          <text-field
            name="other-title"
            :id="'aboutYouOtherTitle'"
            :label="aboutYouOtherTitleLabel"
            :required="true"
            :invalid="touched['otherTitle'] && otherTitleError"
            :valid="
              touched['otherTitle'] && otherTitle.length && !otherTitleError
            "
            :validationMessage="
              otherTitleError ? 'Please enter your title' : null
            "
            v-if="showOtherTitle"
            :value="innerOtherTitle"
            @change="valueChange($event, 'otherTitle')"
          >
          </text-field>
        </div>

        <div class="c-form__group">
          <text-field
            name="name"
            :id="'aboutYouFirstName'"
            :label="aboutYouFirstNameLabel"
            :required="true"
            :invalid="touched['firstName'] && firstNameError"
            :valid="touched['firstName'] && firstName.length && !firstNameError"
            :validationMessage="
              firstNameError ? 'Please enter your first name' : null
            "
            :value="innerFirstName"
            @change="valueChange($event, 'firstName')"
          >
          </text-field>
        </div>

        <div class="c-form__group">
          <text-field
            name="surname"
            :id="'aboutYouSurname'"
            :label="aboutYouSurnameLabel"
            :required="true"
            :invalid="touched['surname'] && surnameError"
            :valid="touched['surname'] && surname.length && !surnameError"
            :validationMessage="
              surnameError ? 'Please enter your surname' : null
            "
            :value="innerSurname"
            @change="valueChange($event, 'surname')"
          >
          </text-field>
        </div>

        <div class="c-form__group">
          <text-field
            name="customerReferenceNumber"
            :id="'customerReferenceNumber'"
            :label="aboutYouCustomerReferenceNumberLabel"
            :tooltip="true"
            :tooltipTitle="aboutYouCustomerReferenceNumberLabel"
            :tooltipMessage="aboutYouCustomerReferenceNumberTooltip"
            :isActiveTooltip="isActiveCustomerReferenceNumberTooltip"
            :invalid="
              touched['customerReferenceNumber'] && customerReferenceNumberError
            "
            :valid="
              touched['customerReferenceNumber'] &&
              !customerReferenceNumberError
            "
            :validationMessage="
              customerReferenceNumberError
                ? 'Please enter a valid customer reference number'
                : null
            "
            :value="innerCustomerReferenceNumber"
            @change="valueChange($event, 'customerReferenceNumber')"
            @toggleTooltip="toggleTooltip($event, aboutYouCustomerReferenceNumberLabel)"
          >
          </text-field>
        </div>

        <div class="c-form__group">
          <text-field
            name="email"
            :id="'aboutYouEmail'"
            :required="true"
            :label="aboutYouEmailLabel"
            :tooltip="true"
            :tooltipTitle="aboutYouEmailLabel"
            :tooltipMessage="aboutYouEmailToolTip"
            :isActiveTooltip="isActiveEmailTooltip"
            :invalid="touched['email'] && emailError"
            :valid="touched['email'] && email.length && !emailError"
            :validationMessage="
              emailError ? 'Please enter a valid email address' : null
            "
            :value="innerEmail"
            @change="valueChange($event, 'email')"
            @toggleTooltip="toggleTooltip($event, aboutYouEmailLabel)"
          >
          </text-field>
        </div>

        <div class="c-form__group">
          <text-field
            name="confirmEmail"
            :id="'aboutYouConfirmEmail'"
            :required="true"
            :label="aboutYouConfirmEmailLabel"
            :invalid="touched['confirmEmail'] && confirmEmailError"
            :valid="
              touched['confirmEmail'] &&
              confirmEmail.length &&
              !confirmEmailError
            "
            :validationMessage="
              confirmEmailError ? 'Your email address does not match' : null
            "
            :value="innerConfirmEmail"
            @change="valueChange($event, 'confirmEmail')"
          >
          </text-field>
        </div>

        <div class="c-form__group">
          <dropdown-field
            :inputLabel="aboutYouPrimaryContactLabel"
            id="aboutYouPrimaryContact"
            selectLabel="Please select"
            type="tel"
            autocomplete="mobile tel"
            :options="dropdownOptions"
            :required="true"
            :tooltip="true"
            :tooltipTitle="aboutYouPrimaryContactLabel"
            :tooltipMessage="aboutYouPrimaryContactToolTip"
            :isActiveTooltip="isActivePrimaryContactTooltip"
            :valid="
              (touched['primaryContact'] || touched['primaryContactType']) &&
              primaryContact.length > 0 &&
              primaryContactType.length > 0 &&
              primaryContactFieldIsValid
            "
            :invalid="
              (touched['primaryContact'] || touched['primaryContactType']) &&
              !!primaryContactValidationMsg &&
              !primaryContactFieldIsValid
            "
            :validationMessage="primaryContactValidationMsg"
            :inputValue="innerPrimaryContact"
            :selectValue="innerPrimaryContactType"
            @onInputValue="valueChange($event, 'primaryContact')"
            @onSelectValue="valueChange($event.value, 'primaryContactType')"
            @toggleTooltip="toggleTooltip($event, aboutYouPrimaryContactLabel)"
          ></dropdown-field>
        </div>

        <div class="c-form__group">
          <dropdown-field
            :inputLabel="aboutYouSecondaryContactLabel"
            id="aboutYouSecondaryContact"
            selectLabel="Please select"
            type="tel"
            autocomplete="mobile tel"
            :options="dropdownOptions"
            :valid="
              (touched['secondaryContact'] ||
                touched['secondaryContactType']) &&
              secondaryContactFieldIsValid
            "
            :invalid="
              (touched['secondaryContact'] ||
                touched['secondaryContactType']) &&
              !secondaryContactFieldIsValid
            "
            :validationMessage="secondaryContactValidationMsg"
            :inputValue="innerSecondaryContact"
            :selectValue="innerSecondaryContactType"
            @onInputValue="valueChange($event, 'secondaryContact')"
            @onSelectValue="valueChange($event.value, 'secondaryContactType')"
          ></dropdown-field>
        </div>

        <div class="c-form__group">
          <p v-if="aboutYouAdditionalNeedsDesc">
            {{ aboutYouAdditionalNeedsDesc }}
          </p>
          <textarea-field
            :label="aboutYouAdditionalNeedsLabel"
            id="additionalNeeds"
            name="additionalNeeds"
            :value="innerAdditionalNeeds"
            :invalid="touched['additionalNeeds'] && additionalNeedsError"
            :valid="touched['additionalNeeds'] && !additionalNeedsError"
            :validationMessage="
              additionalNeedsError
                ? 'Please enter less than 1000 characters'
                : null
            "
            :tooltip="true"
            :tooltipTitle="aboutYouAdditionalNeedsLabel"
            :tooltipMessage="aboutYouAdditionalNeedsTooltip"
            :isActiveTooltip="isActiveAdditionalNeedsTooltip"
            @change="valueChange($event, 'additionalNeeds')"
            @toggleTooltip="toggleTooltip($event, aboutYouAdditionalNeedsLabel)"
          />
        </div>
        <action-buttons
          :prevLabel="backButtonText"
          :nextLabel="continueButtonText"
          @onPrev="onPrev"
          @onNext="checkForm"
        />
      </form>
    </div>
  </div>
</template>

<script>
  import ActionButtons from "../../forms/action-buttons/ActionButtons.vue";
  import TextField from "../../forms/input/TextField.vue";
  import FormSelect from "../../forms/select/SelectDropdown.vue";
  import DropdownField from "./DropdownField";
  import TextareaField from "../../forms/textarea/TextareaField.vue";
  export default {
    components: {
      ActionButtons,
      TextField,
      FormSelect,
      DropdownField,
      TextareaField,
    },
    props: {
      aboutYouEmailLabel: {
        type: String,
        default: "Email address",
      },
      aboutYouConfirmEmailLabel: {
        type: String,
        default: "Confirm your Email address",
      },
      aboutYouFirstNameLabel: {
        type: String,
        default: "First name",
      },
      aboutYouOtherTitleLabel: {
        type: String,
        default: "Other",
      },
      aboutYouPrimaryContactLabel: {
        type: String,
        default: "Preferred number",
      },
      aboutYouEmailToolTip: {
        required: true,
        type: String,
      },
      aboutYouPrimaryContactToolTip: {
        required: true,
        type: String,
      },
      aboutYouSecondaryContactLabel: {
        type: String,
        default: "Alternative number",
      },
      aboutYouCustomerReferenceNumberLabel: {
        type: String,
        default: "Customer reference number",
      },
      aboutYouCustomerReferenceNumberTooltip: {
        required: true,
        type: String,
      },
      aboutYouSurnameLabel: {
        type: String,
        default: "Surname",
      },
      aboutYouCustomerReferenceNumberTooltip: {
        required: true,
        type: String,
      },
      aboutYouTitleLabel: {
        type: String,
        default: "Title",
      },
      aboutYouAdditionalNeedsTooltip: {
        required: true,
        type: String,
      },
      aboutYouAdditionalNeedsDesc: {
        type: String,
      },
      aboutYouAdditionalNeedsLabel: {
        type: String,
        default: "Additional needs",
      },
      backButtonText: {
        type: String,
      },
      continueButtonText: {
        type: String,
      },
      email: {
        required: true,
        type: String,
      },
      confirmEmail: {
        required: true,
        type: String,
      },
      firstName: {
        required: true,
        type: String,
      },
      otherTitle: {
        required: true,
        type: String,
      },
      primaryContact: {
        required: true,
        type: String,
      },
      secondaryContact: {
        required: true,
        type: String,
      },
      primaryContactType: {
        required: true,
        type: String,
      },
      secondaryContactType: {
        required: true,
        type: String,
      },
      surname: {
        required: true,
        type: String,
      },
      customerReferenceNumber: {
        required: true,
        type: String,
      },
      additionalNeeds: {
        required: true,
        type: String,
      },
      title: {
        required: true,
        type: String,
      },
      activeTooltipId: {
        type: String,
      },
    },
    watch: {
      email(newVal) {
        this.innerEmail = newVal;
      },
      confirmEmail(newVal) {
        this.innerConfirmEmail = newVal;
      },
      firstName(newVal) {
        this.innerFirstName = newVal;
      },
      innerEmail(newVal) {
        this.valueChange(newVal, "email");
      },
      innerConfirmEmail(newVal) {
        this.valueChange(newVal, "confirmEmail");
      },
      innerFirstName(newVal) {
        this.valueChange(newVal, "firstName");
      },
      innerOtherTitle(newVal) {
        this.valueChange(newVal, "otherTitle");
      },
      innerPrimaryContact(newVal) {
        this.valueChange(newVal, "primaryContact");
      },
      innerSecondaryContact(newVal) {
        this.valueChange(newVal, "secondaryContact");
      },
      innerPrimaryContactType(newVal) {
        this.valueChange(newVal, "primaryContactType");
      },
      innerSecondaryContactType(newVal) {
        this.valueChange(newVal, "secondaryContactType");
      },
      innerSurname(newVal) {
        this.valueChange(newVal, "surname");
      },
      innerCustomerReferenceNumber(newVal) {
        this.valueChange(newVal, "customerReferenceNumber");
      },
      innerAdditionalNeeds(newVal) {
        this.valueChange(newVal, "additionalNeeds");
      },
      innerTitle(newVal) {
        this.valueChange(newVal, "title");
      },
      otherTitle(newVal) {
        this.innerOtherTitle = newVal;
      },
      primaryContact(newVal) {
        this.innerPrimaryContact = newVal;
      },
      secondaryContact(newVal) {
        this.innerSecondaryContact = newVal;
      },
      primaryContactType(newVal) {
        this.innerPrimaryContactType = newVal;
      },
      secondaryContactType(newVal) {
        this.innerSecondaryContactType = newVal;
      },
      surname(newVal) {
        this.innerSurname = newVal;
      },
      customerReferenceNumber(newVal) {
        this.innerCustomerReferenceNumber = newVal;
      },
      additionalNeeds(newVal) {
        this.innerAdditionalNeeds = newVal;
      },
      title(newVal) {
        this.innerTitle = newVal;
      },
    },
    computed: {
      primaryContactFieldIsValid() {
        return (
          this.primaryContact.length > 0 &&
          this.validPhoneNumber(this.primaryContact) &&
          this.primaryContactType.length > 0
        );
      },
      secondaryContactFieldIsValid() {
        if (
          !this.secondaryContact.length > 0 &&
          !this.secondaryContactType.length > 0
        )
          return true;
        return !this.secondaryContactError && !this.secondaryContactTypeError;
      },
      primaryContactValidationMsg() {
        if (this.primaryContactError || this.primaryContactTypeError) {
          return this.primaryContactError
            ? "Please enter a valid contact telephone number"
            : "Please select a contact telephone number type";
        }
        return null;
      },
      secondaryContactValidationMsg() {
        if (this.secondaryContactError || this.secondaryContactTypeError) {
          return this.secondaryContactError
            ? "Please enter a valid alternative contact telephone number"
            : "Please select an alternative contact telephone number type";
        }
        return null;
      },
      emailError() {
        return this.email === "" || this.validEmail(this.email) === false;
      },
      confirmEmailError() {
        return this.confirmEmail === "" || this.email !== this.confirmEmail;
      },
      firstNameError() {
        return this.firstName === "";
      },
      otherTitleError() {
        return this.otherTitle === "" && this.title === "Other";
      },
      primaryContactError() {
        return (
          this.primaryContact === "" ||
          this.validPhoneNumber(this.primaryContact) === false
        );
      },
      primaryContactTypeError() {
        return (
          this.primaryContactType === "" || this.primaryContactType === null
        );
      },
      secondaryContactError() {
        return (
          (this.secondaryContactType.length > 0 &&
            this.secondaryContact === "") ||
          (this.secondaryContact.length > 0 &&
            this.validPhoneNumber(this.secondaryContact) === false)
        );
      },
      secondaryContactTypeError() {
        return (
          this.secondaryContact.length > 0 && this.secondaryContactType === ""
        );
      },
      showOtherTitle() {
        return this.title === "Other";
      },
      surnameError() {
        return this.surname === "";
      },
      customerReferenceNumberError() {
        return !this.validCustomerReferenceNumber(this.customerReferenceNumber);
      },
      titleError() {
        return this.title === "";
      },
      additionalNeedsError() {
        return this.additionalNeeds.length > 1000;
      },
      isActiveCustomerReferenceNumberTooltip() {
        return this.activeTooltipId === this.aboutYouCustomerReferenceNumberLabel
      },
      isActivePrimaryContactTooltip() {
        return this.activeTooltipId === this.aboutYouPrimaryContactLabel
      },
      isActiveEmailTooltip() {
        return this.activeTooltipId === this.aboutYouEmailLabel
      },
      isActiveAdditionalNeedsTooltip() {
        return this.activeTooltipId === this.aboutYouAdditionalNeedsLabel
      }
    },
    created() {
      this.innerEmail = this.email;
      this.innerConfirmEmail = this.confirmEmail;
      this.innerFirstName = this.firstName;
      this.innerPrimaryContact = this.primaryContact;
      this.innerSecondaryContact = this.secondaryContact;
      this.innerPrimaryContactType = this.primaryContactType;
      this.innerSecondaryContactType = this.secondaryContactType;
      this.innerSurname = this.surname;
      this.innerCustomerReferenceNumber = this.customerReferenceNumber;
      this.innerAdditionalNeeds = this.additionalNeeds;
      if (this.title) {
        if (this.titleOptions.find(o => o.value === this.title)) {
          this.innerTitle = this.title;
        } else {
          this.innerTitle = "Other";
          this.innerOtherTitle = this.title;
        }
      }
    },
    data() {
      return {
        innerEmail: "",
        innerConfirmEmail: "",
        innerFirstName: "",
        innerOtherTitle: "",
        innerPrimaryContact: "",
        innerPrimaryContactType: "",
        innerPriorityServices: false,
        innerSecondaryContact: "",
        innerSecondaryContactType: "",
        innerSurname: "",
        innerCustomerReferenceNumber: "",
        innerAdditionalNeeds: "",
        innerTitle: "",
        titleOptions: [
          {
            label: "Mr",
            value: "Mr",
          },
          {
            label: "Mrs",
            value: "Mrs",
          },
          {
            label: "Miss",
            value: "Miss",
          },
          {
            label: "Ms",
            value: "Ms",
          },
          {
            label: "Dr",
            value: "Dr",
          },
          {
            label: "Other",
            value: "Other",
          },
        ],
        dropdownOptions: [
          { label: "Please select", value: "" },
          { label: "Mobile", value: "Mobile" },
          { label: "Landline", value: "Landline" },
        ],
        ddTT: true,
        ddValid: false,
        ddInvalid: false,
        ddValidationMsg: "Sort it out",
        touched: {
          email: false,
          confirmEmail: false,
          firstName: false,
          otherTitle: false,
          primaryContact: false,
          secondaryContact: false,
          primaryContactType: false,
          secondaryContactType: false,
          customerReferenceNumber: false,
          additionalNeeds: false,
          surname: false,
          title: false,
        },
      };
    },
    methods: {
      onPrev() {
        this.$emit("onPrev");
      },
      checkForm() {
        Object.keys(this.touched).forEach((key) => (this.touched[key] = true));
        if (
          this.emailError === false &&
          this.confirmEmailError === false &&
          this.firstNameError === false &&
          this.otherTitleError === false &&
          this.primaryContactError === false &&
          this.primaryContactTypeError === false &&
          this.secondaryContactError === false &&
          this.secondaryContactTypeError === false &&
          this.surnameError === false &&
          this.customerReferenceNumberError === false &&
          this.additionalNeedsError === false &&
          this.titleError === false
        ) {
          this.$emit("collateDetails");
        }
      },
      toggleTooltip(value, id) {
        this.$emit('activeTooltip', value ? id : '')
      },
      validEmail(email) {
        const emailRegex = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
        return emailRegex.test(email);
      },
      validPhoneNumber(phone) {
        return phone.length >= 11 && phone.length <= 15;
      },
      validCustomerReferenceNumber(customerReferenceNumber) {
        if (customerReferenceNumber.length > 0) {
          const customerRefNumRegex = /^\d{8}$/;
          return customerReferenceNumber.length === 8 && customerRefNumRegex.test(customerReferenceNumber);
        }
        return true;
      },
      valueChange($event, key) {
        this.touched[key] = true;
        this.$emit("valueChange", key, $event);
      },
    },
  };
</script>



// WEBPACK FOOTER //
// src/components/components/consumer/_contact-us-rap/_vue/UserDetails.vue